import React, { useState, useEffect, useCallback } from 'react';
import { Container, Table, Badge, Form, Button, Modal, Alert } from 'react-bootstrap';
import api, { userApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

function UserList() {
  //console.log('UserList');
  const { userlist } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm, setEditForm] = useState({
    username: '',
    email: '',
    role: '',
    is_staff: false
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    username: '',
    email: '',
    password: '',
    password2: '',
    role: 'STUDENT',
    is_staff: false
  });

  const fetchUsers = useCallback(async () => {
    //console.log('fetchUsers');
    try {
      setLoading(true);
      const data = await userlist();
      console.log(data);
      setUsers(data);
      setError('');
    } catch (err) {
      setError('無法載入用戶列表');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleEdit = (user) => {
    setSelectedUser(user);
    setEditForm({
      username: user.username,
      email: user.email,
      role: user.role,
      is_staff: user.is_staff
    });
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    try {
      await userApi.updateUser(selectedUser.id, editForm);
      await fetchUsers();
      setShowEditModal(false);
      setSelectedUser(null);
    } catch (err) {
      setError('更新用戶失敗');
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm('確定要刪除此用戶嗎？')) {
      try {
        await userApi.deleteUser(userId);
        await fetchUsers();
      } catch (err) {
        setError('刪除用戶失敗');
      }
    }
  };

  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case 'ADMIN':
        return 'danger';
      case 'COACH':
        return 'success';
      case 'STUDENT':
        return 'info';
      default:
        return 'secondary';
    }
  };

  const getRoleDisplay = (role) => {
    switch (role) {
      case 'ADMIN':
        return '管理員';
      case 'COACH':
        return '教練';
      case 'STUDENT':
        return '學員';
      default:
        return role;
    }
  };

  const handleCreate = async () => {
    try {
      if (createForm.password !== createForm.password2) {
        setError('兩次輸入的密碼不匹配');
        return;
      }

      setLoading(true);
      await userApi.createUser(createForm);
      await fetchUsers();
      setShowCreateModal(false);
      setCreateForm({
        username: '',
        email: '',
        password: '',
        password2: '',
        role: 'STUDENT',
        is_staff: false
      });
    } catch (err) {
      setError(err.response?.data?.message || '創建用戶失敗');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Container><h3>載入中...</h3></Container>;

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>用戶管理</h2>
        <div className="d-flex gap-2">
          <Button 
            variant="success" 
            onClick={() => setShowCreateModal(true)}
          >
            創建用戶
          </Button>
          <Form.Select 
            style={{ width: 'auto' }}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="">所有用戶</option>
            <option value="ADMIN">管理員</option>
            <option value="COACH">教練</option>
            <option value="STUDENT">學員</option>
          </Form.Select>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>ID</th>
            <th>用戶名</th>
            <th>電子郵件</th>
            <th>角色</th>
            <th>員工</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {users.map(user => (
            <tr key={user.id}>
              <td>{user.id}</td>
              <td>{user.username}</td>
              <td>{user.email}</td>
              <td>
                <Badge bg={getRoleBadgeVariant(user.role)}>
                  {getRoleDisplay(user.role)}
                </Badge>
              </td>
              <td>
                <Badge bg={user.is_staff ? "success" : "secondary"}>
                  {user.is_staff ? "是" : "否"}
                </Badge>
              </td>
              <td>
                <Button 
                  variant="outline-primary" 
                  size="sm" 
                  className="me-2"
                  onClick={() => handleEdit(user)}
                >
                  編輯
                </Button>
                <Button 
                  variant="outline-danger" 
                  size="sm"
                  onClick={() => handleDelete(user.id)}
                >
                  刪除
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>編輯用戶</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>用戶名</Form.Label>
              <Form.Control
                type="text"
                value={editForm.username}
                onChange={(e) => setEditForm({...editForm, username: e.target.value})}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>電子郵件</Form.Label>
              <Form.Control
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({...editForm, email: e.target.value})}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>角色</Form.Label>
              <Form.Select
                value={editForm.role}
                onChange={(e) => setEditForm({...editForm, role: e.target.value})}
              >
                <option value="ADMIN">管理員</option>
                <option value="COACH">教練</option>
                <option value="STUDENT">學員</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>員工</Form.Label>
              <Form.Check
                type="switch"
                id="is-staff-switch"
                checked={editForm.is_staff}
                onChange={(e) => setEditForm({...editForm, is_staff: e.target.checked})}
                label={editForm.is_staff ? "是" : "否"}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            取消
          </Button>
          <Button variant="primary" onClick={handleUpdate}>
            保存
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>創建新用戶</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>用戶名稱</Form.Label>
              <Form.Control
                type="text"
                value={createForm.username}
                onChange={(e) => setCreateForm({...createForm, username: e.target.value})}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>電子郵件</Form.Label>
              <Form.Control
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({...createForm, email: e.target.value})}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>密碼</Form.Label>
              <Form.Control
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm({...createForm, password: e.target.value})}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>確認密碼</Form.Label>
              <Form.Control
                type="password"
                value={createForm.password2}
                onChange={(e) => setCreateForm({...createForm, password2: e.target.value})}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>角色</Form.Label>
              <Form.Select
                value={createForm.role}
                onChange={(e) => setCreateForm({...createForm, role: e.target.value})}
              >
                <option value="STUDENT">學員</option>
                <option value="COACH">教練</option>
                <option value="ADMIN">管理員</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>管理員權限</Form.Label>
              <Form.Check
                type="switch"
                id="create-is-staff-switch"
                checked={createForm.is_staff}
                onChange={(e) => setCreateForm({...createForm, is_staff: e.target.checked})}
                label={createForm.is_staff ? "是" : "否"}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            取消
          </Button>
          <Button 
            variant="primary" 
            onClick={handleCreate}
            disabled={loading}
          >
            {loading ? '創建中...' : '創建'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default UserList; 